import React, { ReactNode, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { ButtonV2 } from '@asigna/kit';
import { deserializeTransaction } from '@stacks/transactions';
import cx from 'classnames';
import {
  NoData,
  NotificationCard,
  TransactionDetailsDialog,
  TransactionInfo,
  TransactionStatusLabel,
  WalletWithIcons,
} from 'components';
import { usePermissionWarning, useReadOnlyWarning } from 'hooks';
import useAddress from 'hooks/use-address';
import { useBreakpoints } from 'hooks/use-breakpoints';
import useNetwork from 'hooks/use-network';
import useSafe from 'hooks/use-safe';
import { useSafeCalls } from 'hooks/use-safe-call';
import AlertIcon from 'images/alert.svg';
import { isEmpty, isNull } from 'lodash';
import { toast } from 'sonner';
import { TransactionStatus } from 'store/types';
import { NETWORK } from 'types/enums';
import { SafeTxError, SafeTxResponse } from 'types/SafeTxResponse';
import { formatDate } from 'utils/dateUtils';
import { getHiroNonce } from 'utils/hiroApi';
import { findNonceToExecuteAfterFirst } from 'utils/nonce-tools';
import { getEventFromTransferMemo } from 'utils/stx20util';
import { IMultisigTransaction } from 'utils/transform-multisig-transaction';

import { ActivityTableHeader } from '../ActivityTableHeader';

import { useQueueStyles } from './useQueueStyles';

export function formatContractId(address: string, name: string) {
  return `${address}.${name}`;
}

type Props = {
  queuedTransactionsNonceCountList: { nonce: number; count: number }[];
  queuedTransactions: IMultisigTransaction[];
};

// TODO_R: [RP] QueuedTransactionsTab
// TODO_Y: [PN] 1h
export const Queue = ({ queuedTransactionsNonceCountList, queuedTransactions }: Props) => {
  const { safe, fetchSafeData, nextExecutedNonce, transactions } = useSafe();
  const walletAccount = useAddress() || '';
  const { nativeExecuteTx, nativeSignTx } = useSafeCalls();
  const classes = useQueueStyles();
  const [detailsTx, setDetailsTx] = useState<null | IMultisigTransaction>(null);
  const nonceToExecuteFirst = nextExecutedNonce;
  const nonceToExecuteAfterFirst = useMemo(
    () => findNonceToExecuteAfterFirst(transactions, nonceToExecuteFirst),
    [transactions, nonceToExecuteFirst]
  );
  const [network] = useNetwork();
  const [mempoolNonce, setMempoolNonce] = useState('-10');
  const [lastExecutedNonce, setLastExecutedNonce] = useState('-10');
  const readOnlyWarning = useReadOnlyWarning();
  const { onMobileZone } = useBreakpoints();

  useEffect(
    function syncMempoolNonce() {
      const getData = async () => {
        try {
          if (!Boolean(safe.address)) {
            setMempoolNonce('-10');
          } else {
            const explorerMempoolNonce = await getHiroNonce(safe.address, network as NETWORK)
              .then((data) => data.json())
              // .then((data) => data.last_mempool_tx_nonce);
              .then((data) => data);
            setMempoolNonce(explorerMempoolNonce.last_mempool_tx_nonce ?? '-10');
            setLastExecutedNonce(explorerMempoolNonce.last_executed_tx_nonce ?? '-10');
            // setLastExecutedNonce('12');
          }
        } catch (error) {
          console.error(error);
        }
      };

      getData().then();

      const intervalId = setInterval(() => {
        getData().then();
      }, 10e3);

      return () => {
        clearInterval(intervalId);
      };
    },
    [nonceToExecuteFirst]
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      reloadTransactions().then();
    }, 60e3);

    return () => {
      clearInterval(intervalId);
    };
  }, [safe.address, queuedTransactions]);

  useEffect(() => {
    reloadTransactions().then();
  }, [mempoolNonce]);

  const reloadTransactions = async () => {
    fetchSafeData(safe._id).then();
  };

  const handleSign = (e: SyntheticEvent, transaction: IMultisigTransaction) => {
    e.stopPropagation();
    // setIsSignClicked(transaction._id);
    const signedTx = transaction.rawTransaction;
    nativeSignTx(transaction._id, signedTx, !transaction.signatures.length).then(
      (res: SafeTxResponse) => {
        if (!res.isSuccessful) {
          if (res.error === SafeTxError.FeeHasChanged) {
            toast(
              () => (
                <NotificationCard
                  title="Warning"
                  message="You shouldn't override the transaction gas fees in the connected wallet since all previous signatures will become invalid"
                  type="info"
                />
              ),
              {
                className: 'w-full',
              }
            );
            // setIsSignClicked('');
          }
          // FIXME: xverse cancel error not caught
          if (res.error === SafeTxError.CanceledByUser) {
            // setIsSignClicked('');
          }
        }
      }
    );
  };

  const handleExecute = (e: SyntheticEvent, transaction: IMultisigTransaction) => {
    e.stopPropagation();
    nativeExecuteTx(transaction?._id).then();
  };

  const permissionSignWarning = usePermissionWarning('VOTE');
  const permissionExecuteWarning = usePermissionWarning('EXECUTE');

  // TODO_Y: have a look
  // TODO_R: move into a separate component
  const displayButtons = (transaction: IMultisigTransaction, size: 'sm' | 'md') => {
    // if old tx
    if (transaction.isSigningFlowTx) return;
    // if (transaction.status === TransactionStatus.EXECUTION) return;
    const isFullySigned = safe.threshold === transaction.signatures.length;
    const isSignedByOwner = transaction.signatures.some((sig) => sig.owner === walletAccount);
    const isAbleToSign = !isFullySigned && !isSignedByOwner;
    // const isProcessing = transaction.status === TransactionStatus.EXECUTED && transaction.nonce === +mempoolNonce;
    const isFinalizing =
      transaction.nonce === +lastExecutedNonce &&
      transaction.status === TransactionStatus.EXECUTION;

    if (isFinalizing || transaction.status === TransactionStatus.EXECUTION) return;

    const buttonsToDisplay: ReactNode[] = [];
    if (isAbleToSign)
      buttonsToDisplay.push(
        <ButtonV2
          onClick={(e) => handleSign(e, transaction)}
          size={size}
          disabled={Boolean(readOnlyWarning) || Boolean(permissionSignWarning)}
          disableHint={readOnlyWarning || permissionSignWarning}
          hintPosition="bottom-end"
          className="min-w-92 sm:min-w-[110px]"
        >
          Sign
        </ButtonV2>
      );

    if (isSignedByOwner && !isFullySigned)
      buttonsToDisplay.push(
        <ButtonV2 className="min-w-92 sm:min-w-[110px]" disabled message="Signed" size={size} />
      );

    if (isFullySigned)
      buttonsToDisplay.push(
        <ButtonV2
          onClick={(e) => handleExecute(e, transaction)}
          size={size}
          disabled={Boolean(readOnlyWarning) || Boolean(permissionExecuteWarning)}
          disableHint={readOnlyWarning || permissionExecuteWarning}
          hintPosition="bottom-end"
          className="min-w-92 sm:min-w-[110px]"
        >
          Execute
        </ButtonV2>
      );

    return buttonsToDisplay;
  };

  if (isEmpty(queuedTransactions)) return <NoData />;

  return (
    <>
      <ActivityTableHeader isQueue />
      {queuedTransactionsNonceCountList.map(({ nonce, count }, index) => {
        const isNextConflictTr =
          index <= queuedTransactionsNonceCountList.length - 2
            ? queuedTransactionsNonceCountList[index + 1].count > 1
            : false;

        const isExecutedCurrentNonce =
          queuedTransactions.filter(
            (queuedTransaction) =>
              queuedTransaction.nonce === nonce &&
              queuedTransaction.status === TransactionStatus.EXECUTION
          ).length > 0;

        const isFinalizedCurrentNonce =
          queuedTransactions.filter(
            (queuedTransaction) =>
              queuedTransaction.nonce === nonce &&
              queuedTransaction.nonce === +lastExecutedNonce &&
              queuedTransaction.status === TransactionStatus.EXECUTION
          ).length > 0;

        return (
          <>
            {!isNull(nonceToExecuteAfterFirst) && nonce === nonceToExecuteAfterFirst && (
              <div
                key={`anctq-${count}`}
                className={cx(classes.alertNonConflictTrQueue, classes.queueNonceLabel)}
              >
                {`Queued - transaction with nonce ${
                  nonceToExecuteFirst || 0
                } needs to be executed first`}
              </div>
            )}
            {onMobileZone ? (
              <div
                key={nonce}
                className={count > 1 ? classes.conflictTrQueue : classes.simpleTrQueue}
              >
                {count > 1 &&
                  (nonce === +mempoolNonce || mempoolNonce === '-10') &&
                  !isFinalizedCurrentNonce && (
                    <div className={classes.alertConflictTrQueue}>
                      <img src={AlertIcon} alt="" />
                      These transactions conflict as they use the same nonce. Executing one will
                      automatically replace the other(s)
                    </div>
                  )}

                {queuedTransactions
                  .filter((queuedTransaction) => queuedTransaction.nonce === nonce)
                  .filter(
                    (queuedTransaction) =>
                      queuedTransaction.status === TransactionStatus.QUEUED ||
                      queuedTransaction.status === TransactionStatus.EXECUTION
                  )
                  .map((queuedTransaction) => {
                    const deserializedTx = deserializeTransaction(queuedTransaction.rawTransaction);
                    const memo = (deserializedTx as any).payload.memo;
                    const memoEvent =
                      memo && getEventFromTransferMemo(memo.content.replaceAll('\u0000', ''));
                    const statusExecuting =
                      queuedTransaction.status === TransactionStatus.EXECUTION;

                    const isFinalizing =
                      queuedTransaction.nonce === +lastExecutedNonce &&
                      queuedTransaction.status === TransactionStatus.EXECUTION;

                    return (
                      <div
                        key={queuedTransaction._id}
                        className={classes.txWrapper}
                        onClick={() => setDetailsTx(queuedTransaction)}
                      >
                        <div
                          className={cx(
                            'flex flex-col gap-4 px-16 py-16 text-r12',
                            safe.threshold > 1 ? classes.wrapperActive : '',
                            isNextConflictTr ? '!border-b-0' : 'border-b-[.5px] border-black-4'
                          )}
                        >
                          <div className="flex gap-8 items-center justify-between">
                            <div className={classes.typeContent}>
                              <div className="flex items-center justify-center justify-self-start w-24 h-24 rounded-[24px] ml-0 border border-black-4 shrink-0">
                                <span className="text-r12">{queuedTransaction.nonce}</span>
                              </div>
                              <div className={classes.txType}>
                                {/* TODO_R: have a look */}
                                <TransactionInfo
                                  transaction={queuedTransaction}
                                  memoEvent={memoEvent}
                                  showLink={statusExecuting && Boolean(queuedTransaction.txId)}
                                />
                              </div>
                            </div>
                            <div className="text-r12 text-regular">
                              {formatDate(queuedTransaction.create_time)}
                            </div>
                          </div>
                          <div className="flex gap-8 items-center justify-between">
                            <div className={classes.transactionStatus}>
                              <TransactionStatusLabel
                                loading={statusExecuting}
                                signed={queuedTransaction.signatures.length}
                                threshold={safe.threshold}
                                isFinalizing={isFinalizing}
                                isFinalizedCurrentNonce={isFinalizedCurrentNonce}
                                isExecutedCurrentNonce={isExecutedCurrentNonce}
                              />
                            </div>
                            <div className={classes.buttonsWrapper}>
                              {!(
                                isExecutedCurrentNonce &&
                                isFinalizedCurrentNonce &&
                                !isFinalizing
                              ) && displayButtons(queuedTransaction, 'sm')}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div
                key={nonce}
                className={count > 1 ? classes.conflictTrQueue : classes.simpleTrQueue}
              >
                {count > 1 &&
                  (nonce === +mempoolNonce || mempoolNonce === '-10') &&
                  !isFinalizedCurrentNonce && (
                    <div className={classes.alertConflictTrQueue}>
                      <img src={AlertIcon} alt="" />
                      These transactions conflict as they use the same nonce. Executing one will
                      automatically replace the other(s)
                    </div>
                  )}

                {queuedTransactions
                  .filter((queuedTransaction) => queuedTransaction.nonce === nonce)
                  // TODO_R: переписать в один .filter. И разве они уже не отфильтрованы парентом?
                  // .filter(
                  //   (queuedTransaction) =>
                  //     queuedTransaction.status === TransactionStatus.QUEUED ||
                  //     queuedTransaction.status === TransactionStatus.EXECUTION
                  // )
                  .map((queuedTransaction) => {
                    const deserializedTx = deserializeTransaction(queuedTransaction.rawTransaction);
                    const memo = (deserializedTx as any).payload.memo;
                    const memoEvent =
                      memo && getEventFromTransferMemo(memo.content.replaceAll('\u0000', ''));
                    const statusExecuting =
                      queuedTransaction.status === TransactionStatus.EXECUTION;
                    const displayedFee = queuedTransaction.fee;

                    const isFinalizing =
                      queuedTransaction.nonce === +lastExecutedNonce &&
                      queuedTransaction.status === TransactionStatus.EXECUTION;

                    return (
                      <div
                        key={queuedTransaction._id}
                        className={cx(
                          classes.txWrapper,
                          isExecutedCurrentNonce &&
                            isFinalizedCurrentNonce &&
                            !isFinalizing &&
                            'opacity-50'
                        )}
                        onClick={() => setDetailsTx(queuedTransaction)}
                      >
                        <div
                          className={cx(
                            classes.wrapper,
                            safe.threshold > 1 ? classes.wrapperActive : '',
                            isNextConflictTr && '!border-b-0'
                          )}
                        >
                          <div className={classes.typeContent}>
                            <div className="flex items-center justify-center justify-self-start w-24 h-24 rounded-[24px] ml-0 border border-black-4 shrink-0">
                              <span className="text-r12">{queuedTransaction.nonce}</span>
                            </div>
                            <div className={classes.txType}>
                              {/* TODO_R: have a look */}
                              <TransactionInfo
                                transaction={queuedTransaction}
                                memoEvent={memoEvent}
                                showLink={statusExecuting && Boolean(queuedTransaction.txId)}
                              />
                            </div>
                          </div>
                          <div className={classes.initiatorContent}>
                            <WalletWithIcons address={queuedTransaction.creator} />
                          </div>
                          <div className={classes.dateContent}>
                            {formatDate(queuedTransaction.create_time)}
                          </div>
                          <div className={classes.dateContent}>
                            {Number(displayedFee) / Math.pow(10, 6)}
                            {' STX'}
                          </div>
                          <div className={classes.transactionStatus}>
                            <TransactionStatusLabel
                              loading={statusExecuting}
                              signed={queuedTransaction.signatures.length}
                              threshold={safe.threshold}
                              isFinalizing={isFinalizing}
                              isFinalizedCurrentNonce={isFinalizedCurrentNonce}
                              isExecutedCurrentNonce={isExecutedCurrentNonce}
                            />
                          </div>
                          <div className={classes.buttonsWrapper}>
                            {!(
                              isExecutedCurrentNonce &&
                              isFinalizedCurrentNonce &&
                              !isFinalizing
                            ) && displayButtons(queuedTransaction, 'md')}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </>
        );
      })}
      {detailsTx && (
        <TransactionDetailsDialog
          transactionId={detailsTx._id}
          isProcessing={detailsTx.nonce === +mempoolNonce}
          onClose={() => {
            setDetailsTx(null);
          }}
        />
      )}
    </>
  );
};
